// CatalogView — marketplace catalog listing (facet rail + ProductGrid).
//
// Extracted from MarketplaceEmbed.tsx (Task 2, TD-269 Sub-project D UX split).
// Owns its own catalog fetch/pagination state — the parent router owns the
// FILTER state (search/category/productType/sort, lifted so filters survive
// PDP round-trips) and the shell renders the controls (toolbar) and the hero.
//
// Task 11 (marketplace mini-site redesign):
//   - Fetches via embedApiFetchPage — the envelope-preserving variant that
//     surfaces {pagination, facets} siblings the .data-unwrapping call lost.
//   - Cursor-based load-more: first page on mount/filter change, "Load more"
//     appends the next page while pagination.has_next, hides at the end.
//   - Facet rail (spec D8): Category + Product-type sections built from
//     facets, single-select with an "All" (clear) affordance per facet;
//     each section hides when its facet list is empty. Desktop renders a
//     left rail column; <768px collapses to horizontally-scrolled chip rows.
//   - Reports each loaded page up via onCatalogLoaded so the router can feed
//     the shell's D10 hero (photo tiles + facets.min_price chip).
//
// An unknown or inactive category slug is resolved server-side to an empty
// result with HTTP 200 (never an error) — that flows through the normal
// empty-products render path below, same as a genuinely empty catalog.
//
// Inline styles only — no Tailwind. NO imports from portal/.

import { useState, useEffect, useCallback, useRef, type CSSProperties } from "react";
import { embedApiFetchPage } from "../../embed-shared/api-client";
import { useEmbedTokens } from "../../embed-shared/primitives";
import type { CatalogFacets, CatalogPage, Product } from "../../embed-shared/types";
import { ProductGrid } from "./ProductGrid";
import { typeLabel } from "./ProductCard";
import { useStorefrontConfig } from "./storefront-config";

/**
 * Sort values the backend catalog endpoint accepts. The dropdown in the shell
 * toolbar is the ONLY source of what goes on the wire — the backend 400s on
 * any other value — so its option values must stay byte-identical to this
 * union (see StorefrontShell's SORT_OPTIONS).
 */
export type CatalogSort = "featured" | "name" | "price" | "-price";

/** Page size for the catalog listing (spec §7 — the grid is 3×4 on desktop). */
const CATALOG_PAGE_SIZE = 12;

/** Rail renders as a left column at ≥ this viewport width (mock breakpoint). */
const RAIL_DESKTOP_MIN_WIDTH = 768;

// Shared window-width hook (same pattern as ProductGrid's module-private
// useContainerWidth — inline styles cannot carry media queries, so
// responsive switches re-render on resize). Duplicated locally rather than
// imported so the two components stay independently testable/movable. NOTE
// (final whole-branch review, I-5): ProductGrid's useContainerWidth returns
// VIEWPORT width (window.innerWidth) and is NOT the shared embed-shared/
// use-container-width.ts hook of the same name, which measures the
// CONTAINER via ResizeObserver — that shared hook, not this pattern, is the
// one to use for layout that should track its own container.
function useWindowWidth(): number {
  const [width, setWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1024,
  );
  useEffect(() => {
    const handler = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return width;
}

type PageResult = {
  data: Product[];
  pagination: CatalogPage["pagination"] | null;
  facets: CatalogFacets | null;
};

export interface CatalogViewProps {
  search: string;
  category: string | null;
  /** Active product_type facet filter (raw wire value, e.g. "hardware"). */
  productType: string | null;
  /** Active sort (held by the router; the shell toolbar's select sets it). */
  sort: CatalogSort;
  onCategoryChange: (slug: string | null) => void;
  onProductTypeChange: (value: string | null) => void;
  onProductClick: (slug: string) => void;
  /**
   * Resets the filters (Task 17). Forwarded to ProductGrid's "Clear
   * filters" affordance in the filtered-empty state — the parent owns the
   * filter state, so clearing them is its responsibility, not this view's.
   */
  onClearFilters?: () => void;
  /**
   * Reports each successfully loaded FIRST page (products + facets) up to
   * the router, which feeds the shell's hero (D10 photo tiles + min_price
   * chip) and keeps it rendered across route changes. NOT called for
   * load-more pages (the hero only wants the first page's lead products)
   * nor on fetch errors (the hero keeps its last-known good data).
   */
  onCatalogLoaded?: (products: Product[], facets: CatalogFacets | null) => void;
}

export function CatalogView({
  search,
  category,
  productType,
  sort,
  onCategoryChange,
  onProductTypeChange,
  onProductClick,
  onClearFilters,
  onCatalogLoaded,
}: CatalogViewProps) {
  const t = useEmbedTokens();
  const width = useWindowWidth();
  const isMobile = width < RAIL_DESKTOP_MIN_WIDTH;
  // Operator-edited catalog intro (StoreContent, Task 4). Renders above the
  // grid when configured; undefined = not configured → not rendered (fail-open).
  const config = useStorefrontConfig();

  const [products, setProducts] = useState<Product[]>([]);
  const [facets, setFacets] = useState<CatalogFacets | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [hasNext, setHasNext] = useState(false);
  // Initialize loading=true (TD-314): the fetch effect runs AFTER first paint,
  // so a false initial value paints "No products available." for one frame on
  // every catalog mount (including catalog -> PDP -> back), then flips to the
  // real state. Starting in the loading state paints the skeleton instead.
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Monotonic fetch generation. A load-more that is in flight when the
  // filters/sort change must NOT append its (old-query) page into the
  // (new-query) first page the effect just committed — the unmount/`cancelled`
  // flag only covers the effect's own fetches, not loadMore's. Each fetch
  // captures the current generation and drops its result if a newer one has
  // started.
  const fetchGenerationRef = useRef(0);

  // Base query shared by the first-page fetch and every load-more fetch.
  const baseQuery = useCallback((): Record<string, string | number> => {
    const query: Record<string, string | number> = { limit: CATALOG_PAGE_SIZE, sort };
    if (search) query.search = search;
    if (category) query.category = category;
    if (productType) query.product_type = productType;
    return query;
  }, [search, category, productType, sort]);

  // First page (mount + every filter/sort change). A filter change is a
  // RESET, not an append — cursor state from the previous result is dropped.
  useEffect(() => {
    let cancelled = false;
    const generation = ++fetchGenerationRef.current;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        // Go handler: HandleCatalogList emits {data, pagination, facets}.
        // embedApiFetchPage returns the full envelope (§39 verified against
        // the CatalogPage TS contract). The `?? fallback` mirrors the
        // previous `data ?? []` tolerance: deploy-skew or a proxy edge can
        // hand back a shape missing the envelope without failing the request.
        const res = await embedApiFetchPage<Product[]>("/api/v1/marketplace/catalog", {
          query: baseQuery(),
        });
        const page: PageResult = res ?? { data: [], pagination: null, facets: null };
        if (cancelled || generation !== fetchGenerationRef.current) return;
        setProducts(page.data ?? []);
        setFacets(page.facets);
        setNextCursor(page.pagination?.next_cursor ?? null);
        setHasNext(Boolean(page.pagination?.has_next));
        onCatalogLoaded?.(page.data ?? [], page.facets);
      } catch (err) {
        if (!cancelled && generation === fetchGenerationRef.current) {
          setError(err instanceof Error ? err.message : "Failed to load catalog");
        }
      } finally {
        if (!cancelled && generation === fetchGenerationRef.current) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // onCatalogLoaded is deliberately NOT a dependency: it is a reporting
    // channel, not an input — parent wrappers that re-create the callback
    // each render must not trigger a catalog refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseQuery]);

  // Load-more: appends the next page, carrying the CURRENT cursor. Filter
  // changes reset via the effect above; a load-more still in flight when that
  // happens is invalidated by the generation guard (see fetchGenerationRef).
  const loadMore = useCallback(async () => {
    if (nextCursor == null || loadingMore) return;
    const generation = ++fetchGenerationRef.current;
    setLoadingMore(true);
    try {
      const res = await embedApiFetchPage<Product[]>("/api/v1/marketplace/catalog", {
        query: { ...baseQuery(), cursor: nextCursor },
      });
      // Drop the page if a first-page (filter-change) fetch started while this
      // load-more was in flight — appending an old-query page into a
      // new-query list would interleave mismatched results.
      if (generation !== fetchGenerationRef.current) return;
      const page: PageResult = res ?? { data: [], pagination: null, facets: null };
      setProducts((prev) => [...prev, ...(page.data ?? [])]);
      setNextCursor(page.pagination?.next_cursor ?? null);
      setHasNext(Boolean(page.pagination?.has_next));
    } catch (err) {
      if (generation === fetchGenerationRef.current) {
        setError(err instanceof Error ? err.message : "Failed to load catalog");
      }
    } finally {
      // UNCONDITIONAL (fix round 1): a superseded load-more's in-flight flag
      // is moot — if a filter change won the race, the first-page effect
      // owns the UI now, and leaving this true would wedge the button
      // (permanently-disabled "Loading…"; loadMore() no-ops on its own
      // guard) until the component remounts. Gating the append on the
      // generation above is what protects correctness; this flag is purely
      // this button's own in-flight indicator and must always clear when its
      // fetch settles, whichever generation won.
      setLoadingMore(false);
    }
  }, [nextCursor, loadingMore, baseQuery]);

  // -- Facet rail (D8) ---------------------------------------------------

  const railStyle: CSSProperties = isMobile
    ? { display: "flex", flexDirection: "row", flexWrap: "nowrap", gap: t.spacing.lg, overflowX: "auto", marginBottom: t.spacing.md, paddingBottom: t.spacing.xs }
    : { display: "flex", flexDirection: "column", gap: t.spacing.lg };

  const groupHeadingStyle: CSSProperties = {
    fontSize: t.fontSize.xs,
    fontWeight: 600,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: t.colors.textSecondary,
    margin: `0 0 ${t.spacing.xs}px 0`,
  };

  // Category rows (mock .cat-item): full-width text rows with the count on
  // the far side. On mobile the rows collapse into the horizontal chip strip.
  const categoryRowStyle = (pressed: boolean): CSSProperties => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: t.spacing.sm,
    flex: isMobile ? "0 0 auto" : undefined,
    whiteSpace: "nowrap",
    fontFamily: "inherit",
    fontSize: 13,
    textAlign: "left",
    cursor: "pointer",
    background: pressed ? t.colors.primaryTint : "none",
    border: "1px solid transparent",
    color: pressed ? t.colors.link : t.colors.textSecondary,
    fontWeight: pressed ? 600 : 400,
    padding: `${7}px 10px`,
    borderRadius: t.borderRadiusControl,
  });

  // Type chips (mock .chip): pill chips, single-select, with canonical D1
  // labels (typeLabel) + counts.
  const typeChipStyle = (pressed: boolean): CSSProperties => ({
    fontFamily: "inherit",
    fontSize: 12.5,
    fontWeight: 600,
    cursor: "pointer",
    border: `1px solid ${pressed ? t.colors.primary : t.colors.border}`,
    background: pressed ? t.colors.primaryTint : t.colors.cardBg,
    color: pressed ? t.colors.link : t.colors.textSecondary,
    padding: "5px 12px",
    borderRadius: 999,
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    whiteSpace: "nowrap",
  });

  const countStyle: CSSProperties = {
    fontSize: 11,
    // TD-575 (Task 12): secondary text, not the placeholder tone (2.61:1 light / 3.55:1 dark).
    color: t.colors.textSecondary,
  };

  const categories = facets?.categories ?? [];
  const productTypes = facets?.product_types ?? [];

  const rail = (
    <aside aria-label="Filters" style={railStyle}>
      {categories.length > 0 && (
        <section aria-label="Categories" style={{ flex: isMobile ? "0 0 auto" : undefined }}>
          <h4 style={groupHeadingStyle}>Categories</h4>
          <div
            style={{
              display: "flex",
              flexDirection: isMobile ? "row" : "column",
              flexWrap: isMobile ? "nowrap" : undefined,
              gap: 2,
            }}
          >
            <button
              type="button"
              aria-label="All categories"
              aria-pressed={category == null}
              onClick={() => onCategoryChange(null)}
              style={categoryRowStyle(category == null)}
            >
              <span>All</span>
            </button>
            {categories.map((c) => (
              <button
                key={c.id}
                type="button"
                aria-pressed={category === c.slug}
                onClick={() => onCategoryChange(c.slug)}
                style={categoryRowStyle(category === c.slug)}
              >
                <span>{c.name}</span>
                <span style={countStyle}>{c.product_count}</span>
              </button>
            ))}
          </div>
        </section>
      )}
      {productTypes.length > 0 && (
        <section aria-label="Product type" style={{ flex: isMobile ? "0 0 auto" : undefined }}>
          <h4 style={groupHeadingStyle}>Product type</h4>
          <div style={{ display: "flex", flexDirection: isMobile ? "row" : "row", flexWrap: "wrap", gap: t.spacing.sm }}>
            <button
              type="button"
              aria-label="All product types"
              aria-pressed={productType == null}
              onClick={() => onProductTypeChange(null)}
              style={typeChipStyle(productType == null)}
            >
              <span>All</span>
            </button>
            {productTypes.map((pt) => (
              <button
                key={pt.value}
                type="button"
                aria-pressed={productType === pt.value}
                onClick={() => onProductTypeChange(pt.value)}
                style={typeChipStyle(productType === pt.value)}
              >
                <span>{typeLabel(pt.value)}</span>
                <span style={countStyle}>{pt.count}</span>
              </button>
            ))}
          </div>
        </section>
      )}
    </aside>
  );

  return (
    <>
      {config.catalog_intro ? (
        // Operator directive 2026-08-16: the gap BELOW the intro sentence
        // mirrors the gap ABOVE it (the toolbar's marginBottom lg = the same
        // token) — equal breathing room around the sentence.
        <p style={{ color: t.colors.textSecondary, margin: `0 0 ${t.spacing.lg}px` }}>
          {config.catalog_intro}
        </p>
      ) : null}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "232px 1fr",
          gap: t.spacing.lg,
          alignItems: "start",
        }}
      >
        {rail}
        <div>
          <ProductGrid
            products={products}
            loading={loading}
            error={error}
            filtered={Boolean(search || category || productType)}
            onClearFilters={onClearFilters}
            onProductClick={(p) => onProductClick(p.slug)}
          />
          {hasNext && !loading && !error && (
            // raw <button>: custom centered load-more control below the grid;
            // EmbedButton forces its own width/weight chrome. type="button"
            // per §73 (no form owner here, but the rule is at-birth).
            <button
              type="button"
              onClick={() => void loadMore()}
              disabled={loadingMore}
              style={{
                margin: `${t.spacing.md}px auto 0`,
                display: "block",
                fontFamily: "inherit",
                fontSize: t.fontSize.sm,
                fontWeight: 600,
                cursor: loadingMore ? "default" : "pointer",
                color: t.colors.text,
                background: t.colors.cardBg,
                border: `1px solid ${t.colors.border}`,
                borderRadius: t.borderRadiusControl,
                padding: `${t.spacing.xs}px ${t.spacing.md}px`,
              }}
            >
              {loadingMore ? "Loading…" : "Load more"}
            </button>
          )}
        </div>
      </div>
    </>
  );
}
